/// <reference lib="dom" />
/**
 * Encrypted channel that wraps a WebSocket-like transport.
 *
 * Handles ECDH handshake and encrypts/decrypts all messages.
 * Works identically for daemon and client sides.
 */

import {
  generateKeyPair,
  exportPublicKey,
  importPublicKey,
  deriveSharedKey,
  encrypt,
  decrypt,
  type KeyPair,
  type SharedKey,
} from "./crypto.js";
import { arrayBufferToBase64, base64ToArrayBuffer } from "./base64.js";

export interface Transport {
  send(data: string | ArrayBuffer): void;
  close(code?: number, reason?: string): void;
  onmessage: ((data: string | ArrayBuffer) => void) | null;
  onclose: ((code: number, reason: string) => void) | null;
  onerror: ((error: Error) => void) | null;
}

export interface EncryptedChannelEvents {
  onopen?: () => void;
  onmessage?: (data: string | ArrayBuffer) => void;
  onclose?: (code: number, reason: string) => void;
  onerror?: (error: Error) => void;
}

type ChannelState = "connecting" | "handshaking" | "open" | "closed";

type EncryptedChannelOptions = {
  /**
   * If set, the channel can validate repeated plaintext `{type:"e2ee_hello"}`
   * messages even after it is open.
   *
   * This is useful for robustness when the client retries the handshake
   * (e.g., it didn't observe the daemon's `{type:"e2ee_ready"}` yet). In that case,
   * the daemon should re-send `{type:"e2ee_ready"}` without changing keys.
   */
  daemonKeyPair?: KeyPair;
  /**
   * The peer's X25519 public key (base64), captured at handshake time.
   * Daemon side only — populated automatically by `createDaemonChannel`
   * after the client's `e2ee_hello`. Used downstream to look up the
   * device's owning user via the auth-server allow-list (multi-tenant
   * session isolation). Empty string when unknown.
   */
  peerPublicKeyB64?: string;
};

interface E2EEHelloMessage {
  type: "e2ee_hello";
  key: string;
}

interface E2EEReadyMessage {
  type: "e2ee_ready";
}

function buildInvalidHelloError(rawText: string, parsed?: unknown): Error {
  const parsedRecord =
    parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  const rawType = parsedRecord?.type;
  const receivedType =
    typeof rawType === "string" ? rawType : rawType === undefined ? "undefined" : typeof rawType;
  const hasKey = typeof parsedRecord?.key === "string" && parsedRecord.key.trim().length > 0;
  const compact = rawText.replace(/\s+/g, " ").trim();
  const preview = compact.length > 160 ? `${compact.slice(0, 157)}...` : compact;
  return new Error(
    `Invalid hello message (receivedType=${receivedType}, hasKey=${hasKey}, preview=${JSON.stringify(preview)})`,
  );
}

const HANDSHAKE_RETRY_MS = 1000;
const MAX_PENDING_SENDS = 200;

/**
 * Creates an encrypted channel as the initiator (client).
 *
 * The client:
 * 1. Receives daemon's public key via QR code
 * 2. Uses its long-lived identity keypair (preferred), or generates an
 *    ephemeral one as fallback
 * 3. Sends e2ee_hello with own public key
 * 4. Derives shared key and starts encrypted communication
 *
 * Callers SHOULD pass `staticKeyPair` when they have a persisted long-lived
 * identity key (the device-account-key for app clients). The daemon resolves
 * that pubkey to a userId via the auth-server allow-list — without it, the
 * daemon-side strict auth check rejects the connection. The ephemeral
 * fallback is for unauthenticated / pre-auth contexts (e.g. the relay-side
 * test harness, or CLI clients that don't have a device-account-key yet).
 */
export async function createClientChannel(
  transport: Transport,
  daemonPublicKeyB64: string,
  events: EncryptedChannelEvents = {},
  staticKeyPair?: KeyPair,
): Promise<EncryptedChannel> {
  const keyPair = staticKeyPair ?? generateKeyPair();
  const daemonPublicKey = importPublicKey(daemonPublicKeyB64);
  const sharedKey = deriveSharedKey(keyPair.secretKey, daemonPublicKey);

  const channel = new EncryptedChannel(transport, sharedKey, events);

  // Send e2ee_hello with our public key
  const ourPublicKeyB64 = exportPublicKey(keyPair.publicKey);
  const hello: E2EEHelloMessage = { type: "e2ee_hello", key: ourPublicKeyB64 };
  const helloText = JSON.stringify(hello);

  let retry: ReturnType<typeof setInterval> | null = null;
  const emitSendError = (error: unknown) => {
    const err = error instanceof Error ? error : new Error(String(error));
    events.onerror?.(err);
  };
  const sendHello = () => {
    try {
      transport.send(helloText);
      return true;
    } catch (error) {
      // This can happen during daemon restarts while the socket transitions
      // through CLOSING/CLOSED states. Report it but do not throw from timers.
      emitSendError(error);
      return false;
    }
  };
  const clearRetry = () => {
    if (retry) {
      clearInterval(retry);
      retry = null;
    }
  };

  channel.onTransitionToOpen(() => clearRetry());
  channel.onClose(() => clearRetry());

  sendHello();
  retry = setInterval(() => {
    if (channel.isOpen()) {
      clearRetry();
      return;
    }
    sendHello();
  }, HANDSHAKE_RETRY_MS);
  // Avoid keeping Node processes alive (e.g. tests) if the handshake is stuck.
  (retry as unknown as { unref?: () => void }).unref?.();

  return channel;
}

/**
 * Creates an encrypted channel as the responder (daemon).
 *
 * The daemon:
 * 1. Has pre-generated keypair (public key was in QR)
 * 2. Waits for client's e2ee_hello with their public key
 * 3. Derives shared key and starts encrypted communication
 */
export async function createDaemonChannel(
  transport: Transport,
  daemonKeyPair: KeyPair,
  events: EncryptedChannelEvents = {},
): Promise<EncryptedChannel> {
  return new Promise((resolve, reject) => {
    const bufferedMessages: Array<string | ArrayBuffer> = [];
    const shouldIgnorePostHelloPlaintext = (data: string | ArrayBuffer): boolean => {
      try {
        const text = typeof data === "string" ? data : new TextDecoder().decode(data);
        const parsed = JSON.parse(text) as Partial<E2EEHelloMessage | E2EEReadyMessage>;
        return parsed.type === "e2ee_hello" || parsed.type === "e2ee_ready";
      } catch {
        return false;
      }
    };

    transport.onmessage = async (data) => {
      try {
        const helloText = typeof data === "string" ? data : new TextDecoder().decode(data);

        let parsed: unknown;
        try {
          parsed = JSON.parse(helloText);
        } catch {
          throw buildInvalidHelloError(helloText);
        }

        const msg = parsed as Partial<E2EEHelloMessage>;
        if (msg.type !== "e2ee_hello" || typeof msg.key !== "string" || !msg.key.trim()) {
          throw buildInvalidHelloError(helloText, parsed);
        }

        // Buffer any subsequent messages that arrive while we're doing async
        // WebCrypto work to derive the shared key. Without this, it's possible
        // for the next message (already encrypted) to be misinterpreted as a
        // second hello, causing the handshake to fail.
        transport.onmessage = (next) => {
          bufferedMessages.push(next);
        };

        const clientPublicKey = importPublicKey(msg.key);
        const sharedKey = deriveSharedKey(daemonKeyPair.secretKey, clientPublicKey);

        const channel = new EncryptedChannel(transport, sharedKey, events, {
          daemonKeyPair,
          // Stash the client's pubkey verbatim from the hello so upstream
          // code can resolve it to a userId via the auth-server allow-list.
          peerPublicKeyB64: msg.key,
        });
        transport.send(JSON.stringify({ type: "e2ee_ready" } satisfies E2EEReadyMessage));

        channel.setState("open");
        events.onopen?.();

        for (const buffered of bufferedMessages) {
          if (shouldIgnorePostHelloPlaintext(buffered)) continue;
          transport.onmessage?.(buffered);
        }

        resolve(channel);
      } catch (error) {
        reject(error);
      }
    };

    transport.onerror = (error) => {
      reject(error);
    };

    transport.onclose = (code, reason) => {
      reject(new Error(`Connection closed during handshake: ${code} ${reason}`));
    };
  });
}

/**
 * Encrypted channel that wraps a transport with E2EE.
 */
export class EncryptedChannel {
  private transport: Transport;
  private sharedKey: SharedKey;
  private state: ChannelState = "handshaking";
  private events: EncryptedChannelEvents;
  private options: EncryptedChannelOptions;
  private pendingSends: Array<string | ArrayBuffer> = [];
  private inflightSends: Promise<void> = Promise.resolve();
  private onOpenCallbacks: Array<() => void> = [];
  private onCloseCallbacks: Array<() => void> = [];

  constructor(
    transport: Transport,
    sharedKey: SharedKey,
    events: EncryptedChannelEvents = {},
    options: EncryptedChannelOptions = {},
  ) {
    this.transport = transport;
    this.sharedKey = sharedKey;
    this.events = events;
    this.options = options;

    transport.onmessage = (data) => this.handleMessage(data);
    transport.onclose = (code, reason) => {
      this.state = "closed";
      this.events.onclose?.(code, reason);
      for (const cb of this.onCloseCallbacks) cb();
    };
    transport.onerror = (error) => {
      this.events.onerror?.(error);
    };
  }

  setState(state: ChannelState): void {
    this.state = state;
  }

  private async handleMessage(data: string | ArrayBuffer): Promise<void> {
    if (this.state === "handshaking") {
      try {
        const text = typeof data === "string" ? data : new TextDecoder().decode(data);
        const msg = JSON.parse(text) as Partial<E2EEReadyMessage>;
        if (msg.type === "e2ee_ready") {
          this.state = "open";
          this.events.onopen?.();
          for (const cb of this.onOpenCallbacks) cb();
          await this.flushPendingSends();
        }
      } catch {
        // ignore non-ready handshake traffic
      }
      return;
    }

    if (this.state !== "open") return;

    try {
      const ciphertext = await (async () => {
        // Handle (or ignore) any stray plaintext handshake traffic.
        try {
          const text = typeof data === "string" ? data : new TextDecoder().decode(data);
          if (text.trim().startsWith("{")) {
            const parsed = JSON.parse(text) as Partial<E2EEHelloMessage | E2EEReadyMessage>;

            if (parsed.type === "e2ee_hello" && typeof parsed.key === "string") {
              if (this.options.daemonKeyPair) {
                try {
                  const clientPublicKey = importPublicKey(parsed.key);
                  const nextSharedKey = deriveSharedKey(
                    this.options.daemonKeyPair.secretKey,
                    clientPublicKey,
                  );

                  // If it's the same client key (handshake retry), re-send
                  // "ready" but do not re-key. Re-keying here would desync
                  // the channel and cause decrypt failures.
                  if (keysEqual(nextSharedKey, this.sharedKey)) {
                    this.transport.send(
                      JSON.stringify({ type: "e2ee_ready" } satisfies E2EEReadyMessage),
                    );
                    return null;
                  }

                  // Different key implies a new client connection (common with relays
                  // where the daemon's socket stays open while the client reconnects).
                  // Re-key and re-send "ready". Drop any queued sends to avoid leaking
                  // messages between logical client sessions.
                  this.state = "handshaking";
                  this.sharedKey = nextSharedKey;
                  this.pendingSends = [];
                  this.transport.send(
                    JSON.stringify({ type: "e2ee_ready" } satisfies E2EEReadyMessage),
                  );
                  this.state = "open";
                  await this.flushPendingSends();
                  return null;
                } catch (error) {
                  throw error;
                }
              }
              return null;
            }

            if (parsed.type === "e2ee_ready") {
              return null;
            }

            // Any other JSON-looking payload is plaintext app traffic, which
            // means the peer is not encrypting (or we are out of sync).
            throw new Error("Received plaintext frame on encrypted channel");
          }
        } catch (error) {
          // If we detected plaintext protocol mismatch, fail hard.
          if (error instanceof Error && error.message.includes("plaintext frame")) {
            throw error;
          }
          // Otherwise ignore JSON parse/TextDecoder failures and fall back to
          // decoding ciphertext below.
        }

        if (typeof data === "string") {
          return base64ToArrayBuffer(data);
        }

        // Some WebSocket implementations deliver text frames as ArrayBuffer.
        // Our protocol always transmits ciphertext as base64 text.
        try {
          const decoded = new TextDecoder().decode(data);
          return base64ToArrayBuffer(decoded);
        } catch {
          return data;
        }
      })();

      if (ciphertext) {
        const plaintext = await decrypt(this.sharedKey, ciphertext);
        this.events.onmessage?.(plaintext);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));

      // Treat decryption/protocol errors as fatal so the peer can reconnect and
      // re-handshake. Emitting an error event here can cause higher-level code
      // to tear down the session without triggering a clean reconnect.
      try {
        this.transport.close(1011, err.message);
      } catch {
        // ignore
      }
    }
  }

  async send(data: string | ArrayBuffer): Promise<void> {
    if (this.state === "handshaking") {
      if (this.pendingSends.length >= MAX_PENDING_SENDS) {
        this.pendingSends.shift();
      }
      this.pendingSends.push(data);
      return;
    }

    if (this.state !== "open") {
      throw new Error("Channel not open");
    }

    const sendPromise = (async () => {
      const ciphertext = await encrypt(this.sharedKey, data);
      // Send as base64 for WebSocket text compatibility
      this.transport.send(arrayBufferToBase64(ciphertext));
    })();
    // Track in-flight sends so close() can flush them before closing the
    // transport — encryption is async, so a synchronous close would otherwise
    // beat the final frames onto the wire (e.g. the host-exec exit frame).
    this.inflightSends = this.inflightSends.then(
      () => sendPromise.catch(() => {}),
      () => sendPromise.catch(() => {}),
    );
    return sendPromise;
  }

  private async flushPendingSends(): Promise<void> {
    if (this.state !== "open") return;
    const pending = this.pendingSends;
    this.pendingSends = [];
    for (const item of pending) {
      await this.send(item);
    }
  }

  close(code = 1000, reason = "Normal closure"): void {
    this.state = "closed";
    const doClose = () => {
      try {
        this.transport.close(code, reason);
      } catch {
        // ignore
      }
    };
    this.inflightSends.then(doClose, doClose);
  }

  isOpen(): boolean {
    return this.state === "open";
  }

  /**
   * Peer's X25519 public key (base64) captured during the daemon-side
   * handshake. Returns `null` on the client side or when the channel was
   * built without this metadata (legacy code paths). Used by the daemon's
   * WS-server to resolve the connecting device → owning user.
   */
  getPeerPublicKeyB64(): string | null {
    const v = this.options.peerPublicKeyB64;
    return v && v.length > 0 ? v : null;
  }

  onTransitionToOpen(cb: () => void): void {
    this.onOpenCallbacks.push(cb);
  }

  onClose(cb: () => void): void {
    this.onCloseCallbacks.push(cb);
  }
}

function keysEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.byteLength !== b.byteLength) return false;
  for (let i = 0; i < a.byteLength; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
